import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiClientService } from './api-client.service';
import { TrackingService } from './tracking.service';
import { IUser } from '@bdeo-platform/private-api';
import { BdeoToastService } from '@bdeo/webcomponents';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { AsyncApiService } from './async-api.service';

type LoginResponse = {
  access_token: string;
  entity: {
    access_token: string;
    company_id: string;
    id: string;
    email: string;
    company_code: string;
  };
  expired: boolean;
  success: boolean;
};

export const LOGIN_URL_COOKIE_NAME = 'loginUrl';
export const ORIGIN_URL_COOKIE_NAME = 'originApp';
export const AUTH_TOKEN_COOKIE_NAME = 'access_token';
export const USER_ID_LOCAL_STORAGE_KEY = 'user_id';
export const COMPANY_ID_LOCAL_STORAGE_KEY = 'company_id';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiClientService = inject(ApiClientService);
  private readonly i18NextService = inject(I18NEXT_SERVICE);
  private readonly trackingService = inject(TrackingService);
  private readonly cookieService = inject(CookieService);
  private readonly httpClient = inject(HttpClient);
  private readonly asyncApiService = inject(AsyncApiService);

  private accessToken = '';
  private userID = '';
  private currentUser?: IUser;

  public readonly isAuthenticated$ = new ReplaySubject<boolean>();
  private loginUrl = this.cookieService.get(LOGIN_URL_COOKIE_NAME) || environment.loginUrl;

  public getAccessToken(): string {
    return this.cookieService.get(AUTH_TOKEN_COOKIE_NAME) || this.accessToken;
  }

  public getUserID(): string {
    return localStorage.getItem(USER_ID_LOCAL_STORAGE_KEY) || this.userID;
  }

  public isAuthenticated(): boolean {
    return Boolean(this.getAccessToken().length);
  }

  public updateLoginState(): void {
    this.isAuthenticated$.next(Boolean(this.getAccessToken()));
  }

  public getLoginUrl(): string {
    return `${this.loginUrl}/from/brokers`;
  }

  public navigateToExternalUrl(url: string): void {
    setTimeout(() => {
      console.log('navigateToExternalUrl', url);
      window.location.href = url;
    }, 500);
  }

  saveCookie(name: string, value: string): void {
    this.cookieService.set(name, value, this.cookieConfig());
  }

  deleteCookie(name: string): void {
    const { domain, secure, path, sameSite }: CookieOptions = this.cookieConfig();
    this.cookieService.delete(name, path, domain, secure, sameSite);
  }

  cookieConfig(): CookieOptions {
    return {
      domain: environment.cookieDomain,
      secure: true,
      path: '/',
      sameSite: 'Lax'
    };
  }

  public logOut(): void {
    this.deleteCookie(LOGIN_URL_COOKIE_NAME);
    this.deleteCookie(AUTH_TOKEN_COOKIE_NAME);
    localStorage.removeItem(COMPANY_ID_LOCAL_STORAGE_KEY);
    localStorage.removeItem(USER_ID_LOCAL_STORAGE_KEY);

    setTimeout(() => {
      // console.log('logOut', loginUrl);
      this.navigateToExternalUrl(this.getLoginUrl());
    }, 1000);
  }

  public async checkAccessToken(accessToken: string): Promise<void> {
    try {
      const requestBody = {
        access_token: accessToken
      };

      const requestHeaders = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      };

      const response = await firstValueFrom(this.httpClient.post<LoginResponse>(`${environment.baseUrl}/me`, requestBody, requestHeaders));

      if (response.entity) {
        const { email, access_token: accessToken, company_id: companyId, company_code: companyCode, id: userId } = response.entity;

        this.accessToken = accessToken;
        this.userID = userId;

        const { baseUrl } = environment;

        localStorage.setItem(COMPANY_ID_LOCAL_STORAGE_KEY, companyId);
        localStorage.setItem(USER_ID_LOCAL_STORAGE_KEY, userId);

        this.trackingService.identify(userId, email, companyCode, companyId);

        this.apiClientService.configure(baseUrl, this.accessToken);
        this.asyncApiService.setDomain(baseUrl);
        this.isAuthenticated$.next(true);
      } else {
        this.isAuthenticated$.next(false);
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw new Error(error.error.errorMessage);
      }
      throw error;
    }
  }

  public async getUser(): Promise<IUser> {
    try {
      const response = await this.apiClientService.getApiClient().platform.user.get({
        access_token: this.getAccessToken(),
        userId: this.getUserID()
      });

      if (!response.ok || !response.data.success) {
        throw new Error('Error while getting user');
      }

      this.currentUser = response.data.entity;

      return response.data.entity;
    } catch (error) {
      BdeoToastService.error(this.i18NextService.t('brokers:errors.get_user_error'));
      throw error;
    }
  }

  public getCurrentUser(): IUser | undefined {
    return this.currentUser;
  }
}
